/** Step 4: which gates accept this ticket. Writes each changed gate's redeemable_tokens. */
import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { Link } from "react-router-dom";
import { createApolloClient } from "../../../../providers/ApolloClientFactory";
import { userStore } from "../../../../store/user";
import { UPDATE_LOCATION } from "../../../locations/mutations";
import { parseTicketModifiers, permutationTokenNames } from "../../../tokens/variantName";
import type { StepProps } from "../TicketingSetupPage";
import { ACCESS_CONTROL_TYPE } from "../types";
import { Card, CheckRow, Chip, StepFooter } from "../ui";
import { NewGateForm } from "./NewGateForm";

const client = createApolloClient();
const { getState: getUserState } = userStore;

type Mode = "none" | "any" | "some";

export const GatesStep = ({ data, refresh, ticket, ticketId, checks }: StepProps) => {
  const t = ticket!;
  const gates = useMemo(() => data.gates.filter((g) => g.location_type === ACCESS_CONTROL_TYPE), [data.gates]);
  const base = t.name.trim();
  const perms = useMemo(() => {
    const groups = parseTicketModifiers(t.modifiers);
    return groups.length ? permutationTokenNames([t.name], groups) : [];
  }, [t.name, t.modifiers]);
  const mine = useMemo(() => new Set([base, ...perms]), [base, perms]);

  // Per gate: which of this ticket's names it lists. Other names on the gate are untouched.
  const [selected, setSelected] = useState<Record<number, Set<string>>>({});
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [saving, setSaving] = useState(false);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    const next: Record<number, Set<string>> = {};
    for (const g of gates) next[g.id] = new Set((g.redeemable_tokens ?? []).map((n) => String(n).trim()).filter((n) => mine.has(n)));
    setSelected(next);
  }, [gates, mine]);

  const modeOf = (gateId: number): Mode => {
    const s = selected[gateId] ?? new Set<string>();
    if (s.has(base)) return "any";
    return s.size ? "some" : "none";
  };
  const setMode = (gateId: number, mode: Mode) =>
    setSelected((p) => {
      const s = new Set(p[gateId] ?? []);
      if (mode === "none") s.clear();
      else if (mode === "any") { s.clear(); s.add(base); }
      else { s.delete(base); }
      return { ...p, [gateId]: s };
    });
  const togglePerm = (gateId: number, name: string, on: boolean) =>
    setSelected((p) => { const s = new Set(p[gateId] ?? []); s.delete(base); if (on) s.add(name); else s.delete(name); return { ...p, [gateId]: s }; });

  const changedGates = gates.filter((g) => {
    const before = new Set((g.redeemable_tokens ?? []).map((n) => String(n).trim()).filter((n) => mine.has(n)));
    const after = selected[g.id] ?? new Set<string>();
    return before.size !== after.size || [...after].some((n) => !before.has(n));
  });

  const save = async () => {
    if (changedGates.length === 0) return;
    setSaving(true);
    try {
      for (const g of changedGates) {
        const others = (g.redeemable_tokens ?? []).map((n) => String(n).trim()).filter((n) => n && !mine.has(n));
        const list = [...others, ...(selected[g.id] ?? [])];
        await client.mutate({ mutation: UPDATE_LOCATION, variables: { id: g.id, input: { redeemable_tokens: list, last_updated_by: getUserState().user?.id } } });
      }
      await client.clearStore();
      toast.success(`Saved ${changedGates.length} gate${changedGates.length === 1 ? "" : "s"}`);
      refresh();
    } catch (err) {
      toast.error("Failed to save gates: " + err);
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const stepChecks = checks.filter((c) => c.step === "gates");
  const scannable = t.is_active && !t.cashless_top_up_item;

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_340px] gap-4 items-start">
      <div className="flex flex-col gap-3">
        <Card title="Gates" subtitle={`Which gates scan "${t.name}". Gates are the Access Control locations on this event.`}>
          {!scannable && <div className="text-xs text-gray-600 bg-gray-50 rounded-md px-3 py-2">This ticket is a wallet top-up or inactive, so no gate scans it.</div>}
          {gates.length === 0 ? (
            <div className="text-sm text-gray-500">No gate exists for this event yet. Create one below and it accepts this ticket from the start.</div>
          ) : (
            <div className="flex flex-col divide-y divide-gray-100">
              {gates.map((g) => {
                const mode = modeOf(g.id);
                const others = (g.redeemable_tokens ?? []).map(String).filter((n) => n && !mine.has(n.trim()));
                const isOpen = expanded.has(g.id);
                const shown = isOpen ? perms : perms.slice(0, 3);
                return (
                  <div key={g.id} className="py-3 flex flex-col gap-2">
                    <div className="flex flex-wrap items-center gap-3">
                      <div className="min-w-[220px]">
                        <div className="text-sm font-medium text-gray-900">
                          {g.name}
                          <Link to={`/locations/${g.id}/edit`} className="ml-2 text-xs font-normal text-primary-700 hover:text-primary-600">Edit gate</Link>
                        </div>
                        <div className="text-xs text-gray-500">{others.length ? `also accepts ${others.length} other ticket type${others.length === 1 ? "" : "s"}` : "accepts nothing else"}</div>
                      </div>
                      <div className="flex flex-wrap gap-3 text-sm text-gray-700">
                        <label className="flex items-center gap-1.5"><input type="radio" name={`gate-${g.id}`} checked={mode === "none"} onChange={() => setMode(g.id, "none")} /> Does not accept it</label>
                        <label className="flex items-center gap-1.5"><input type="radio" name={`gate-${g.id}`} checked={mode === "any"} onChange={() => setMode(g.id, "any")} /> Accepts it{perms.length ? " (any combination)" : ""}</label>
                        {perms.length > 0 && <label className="flex items-center gap-1.5"><input type="radio" name={`gate-${g.id}`} checked={mode === "some"} onChange={() => setMode(g.id, "some")} /> Only some combinations</label>}
                      </div>
                    </div>
                    {mode === "some" && (
                      <div className="ml-4 flex flex-col gap-1 text-xs text-gray-700">
                        {shown.map((n) => (
                          <label key={n} className="flex items-center gap-2"><input type="checkbox" checked={(selected[g.id] ?? new Set()).has(n)} onChange={(e) => togglePerm(g.id, n, e.target.checked)} /><span className="font-mono">{n}</span></label>
                        ))}
                        {perms.length > 3 && (
                          <button type="button" className="text-primary-700 text-left" onClick={() => setExpanded((p) => { const n = new Set(p); if (n.has(g.id)) n.delete(g.id); else n.add(g.id); return n; })}>
                            {isOpen ? "Show fewer" : `Show all ${perms.length}`}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {adding ? (
            <NewGateForm
              data={data}
              refresh={refresh}
              initialTokens={scannable ? [base] : []}
              onCancel={() => setAdding(false)}
              onCreated={() => setAdding(false)}
            />
          ) : (
            <div className="flex items-center justify-between">
              <button type="button" className="text-sm text-primary-700 hover:text-primary-600 font-medium" onClick={() => setAdding(true)}>New gate</button>
              <span className="text-xs text-gray-500">Tablets refresh their gate list within a minute of a save.</span>
            </div>
          )}
          {adding && changedGates.length > 0 && (
            <div className="text-xs text-amber-700">Unsaved choices on the gates above are lost when the new gate is created. Continue to Review first to keep them.</div>
          )}
        </Card>
        <StepFooter ticketId={ticketId} back="placement" next="review" nextLabel={changedGates.length ? `Save ${changedGates.length} gate${changedGates.length === 1 ? "" : "s"} and continue to Review` : "Continue to Review"} onNext={save} onSave={save} busy={saving} />
      </div>
      <Card title="Checks for this step">
        {stepChecks.length === 0 ? <div className="text-sm text-gray-500">Nothing to report.</div> : stepChecks.map((c, i) => <CheckRow key={i} check={c} ticketId={ticketId} />)}
        {perms.length > 0 && <div className="text-xs text-gray-500">This ticket mints {perms.length} combinations. <Chip tone="amber">{perms.length} names</Chip></div>}
      </Card>
    </div>
  );
};
